/**
 * AccessibilityAnnouncer
 *
 * Manages screen reader announcements for game state changes.
 * Uses ARIA live regions to provide real-time updates to assistive technologies.
 */
export class AccessibilityAnnouncer {
  private politeRegion: HTMLElement | null = null;
  private assertiveRegion: HTMLElement | null = null;
  private statusRegion: HTMLElement | null = null;

  constructor() {
    this.initializeLiveRegions();
  }

  /**
   * Initialize ARIA live regions if they exist in the DOM
   */
  private initializeLiveRegions(): void {
    this.politeRegion = document.getElementById('aria-polite');
    this.assertiveRegion = document.getElementById('aria-assertive');
    this.statusRegion = document.getElementById('aria-status');
  }

  /**
   * Announce a message with 'polite' priority (waits for screen reader to finish current announcement)
   */
  announcePolite(message: string): void {
    if (this.politeRegion) {
      this.politeRegion.textContent = message;
    }
  }

  /**
   * Announce a message with 'assertive' priority (interrupts current screen reader announcement)
   */
  announceAssertive(message: string): void {
    if (this.assertiveRegion) {
      this.assertiveRegion.textContent = message;
    }
  }

  /**
   * Update the status region (for ongoing state like score and lives)
   */
  updateStatus(message: string): void {
    if (this.statusRegion) {
      this.statusRegion.textContent = message;
    }
  }

  /**
   * Announce score change
   */
  announceScoreChange(newScore: number, points: number): void {
    this.announcePolite(`Score increased by ${points} to ${newScore}`);
  }

  /**
   * Announce life lost
   */
  announceLifeLost(remainingLives: number): void {
    if (remainingLives === 1) {
      this.announceAssertive(`Life lost! Warning: only 1 life remaining!`);
    } else if (remainingLives > 0) {
      this.announceAssertive(`Life lost! ${remainingLives} lives remaining`);
    } else {
      this.announceAssertive(`Life lost! No lives remaining. Game over!`);
    }
  }

  /**
   * Announce game start
   */
  announceGameStart(): void {
    this.announceAssertive('Game started! Use arrow keys to move, space bar to fire. Good luck!');
  }

  /**
   * Announce game over
   */
  announceGameOver(finalScore: number): void {
    this.announceAssertive(`Game over! Your final score is ${finalScore}. Press space bar to play again.`);
  }

  /**
   * Announce victory
   */
  announceVictory(finalScore: number): void {
    this.announceAssertive(`Congratulations! You won! Final score: ${finalScore}. Press space bar to play again.`);
  }

  /**
   * Announce pause state
   */
  announcePause(isPaused: boolean): void {
    if (isPaused) {
      this.announceAssertive('Game paused');
    } else {
      this.announceAssertive('Game resumed');
    }
  }

  /**
   * Announce wave cleared
   */
  announceWaveCleared(waveNumber: number): void {
    this.announcePolite(`Wave ${waveNumber} cleared! New wave starting.`);
  }

  /**
   * Announce invincibility status
   */
  announceInvincibility(isActive: boolean): void {
    if (isActive) {
      this.announcePolite('Player is temporarily invincible');
    }
  }

  /**
   * Update current game state (score and lives)
   */
  updateGameState(score: number, lives: number): void {
    this.updateStatus(`Score: ${score}, Lives: ${lives}`);
  }

  /**
   * Clear all announcements
   */
  clear(): void {
    if (this.politeRegion) this.politeRegion.textContent = '';
    if (this.assertiveRegion) this.assertiveRegion.textContent = '';
    if (this.statusRegion) this.statusRegion.textContent = '';
  }
}

// Singleton instance
export const accessibilityAnnouncer = new AccessibilityAnnouncer();
